import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7e83a063"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "player_wrap" }
const _hoisted_2 = { key: 0 }

import {
  computed,
  onUnmounted,
  ref,
  watch,
  withDefaults,
} from 'vue'
import { useStore } from 'vuex'
import type { Archive, CameraDetail } from '@/@types/typescript-axios'
import { useCameraList } from '@/js/composables/useCameraList'
import ContentBox from '@/components/molecules/ContentBox'
import WhiteBox from '@/components/atoms/WhiteBox'
import MultiVideosHeader from './components/MultiVideosHeader'
import Videos from './components/Videos'
import PlayerController from '@/components/organisms/PlayerController'
import ZeroItem from '../ZeroItem'
import { formatDateStr, formatSecondStr } from '@/js/utils/filters'
import { getIsoDateStr, getTimeStr, getAddTime, getDateStr } from '@/js/utils/date'
import LiveDisplayTime from '@/components/organisms/LiveDisplayTime'
import ArchiveDisplayTime from '@/components/organisms/ArchiveDisplayTime'
import {
  cancelArchiveRetry,
  mergeArchivesWithCameras,
  requestArchives,
  scheduleArchiveRetry,
  type MultiVideoArchive,
} from './multiVideoArchive'
import {
  cancelPlaybackTimeUpdate,
  getNextHourStartDate,
  getNextMinuteStartDate,
  resolveLoadedArchivePlayback,
  resolvePlaybackTimeUpdate,
  schedulePlaybackTimeUpdate,
  type StartDate,
} from './multiVideoPlayback'
import {
  cancelControllerHide,
  cleanupUiControl,
  scheduleControllerHide,
  syncFullscreenFlag,
  toggleFullscreen,
} from './multiVideoUiControl'

type LoadedArchiveDataPayload = {
  videoElm: { currentTime: number; duration: number }
  hasLoadedVideo?: boolean
}

/**
 * 過去映像が全てロードされたら実行される
 */

const __default__ = {
  name: 'MultiVideos',
}

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    division: { default: 4 }
  },
  setup(__props: any, { expose, emit }: { emit: ({
  changeDivision: [value: number]
  clearFilter: []
}), expose: any, slots: any, attrs: any }) {

const props = __props as { division: number };





const store = useStore()

const permissions = computed<{ live: boolean; archive: boolean }>(
  () => store.getters['auth/permissions']
)
const cameraInfo = computed<{ showDate: boolean }>(() => store.getters['detail/cameraInfo'])
const liveState = computed<boolean>(() => store.getters['multiVideos/liveState'])

const { cameraList, getCameras, currentPage, pageSize, totalCameras } = useCameraList()

const multiVideos = ref<HTMLElement | null>(null)

/**
 * 過去映像最新映像日時を取得(現在時刻より2分前をセット)。
 */
function getLastDate(): StartDate {
  const date = getAddTime(new Date(), -2, 'minute')
  return { date: date, time: getTimeStr(date) }
}

// 過去映像データ
const archiveData = ref<Archive[][] | null>(null)
// 過去映像の読み込み完了のフラグ
const isArchiveLoaded = ref(false)
// コントローラーの表示フラグ
const isShowController = ref(false)
// ツールチップの表示フラグ
const isShowToolTip = ref(false)
// 過去映像の再生時間
const archiveDisplayTime = ref(0)
// 映像の停止フラグ
const isPaused = ref(true)
// 再生スピード
const speed = ref(1)
// 表示する過去映像の日付
const startDate = ref<StartDate>(getLastDate())
// カメラデータ
const displayCameraData = ref<CameraDetail[]>([])
// 表示する映像
const cameras = ref<MultiVideoArchive[] | null>(null)
// 再生の基準になるvideo element
const videoElm = ref<{ currentTime: number; duration: number } | null>(null)
// 映像の長さ
const duration = ref(60)
// 画角変更用のコントローラーの表示フラグ(falseで固定)
const displayToolTipController = ref(false)
// 自動再生用のカウント 60回(60分)で停止する
const autoPlayCount = ref(0)
// ライブカメラの利用
const useLive = ref<boolean | null>(null)
// フルスクリーン表示
const fullScreenFlag = ref(false)
//  無操作をわかるためのtimeout
const mouseActionTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
// 再生時間更新用のタイマー
const playbackTimeUpdateTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
// シークバーを動かした際に過去映像の再生時間を変更するための値
const seekBarValue = ref(0)
// 過去映像の再取得用フラグ
const retriedRequestArchive = ref(false)
// 再生のリトライ用のタイマー
const archivePlayRetryTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
// 過去映像の再読み込みのリトライ回数
const archiveRetryCount = ref(0)
// 過去映像の再読み込み中フラグ
const isArchivePlayRetrying = ref(false)
// controller固定フラグ
const keepControllerFlag = ref(false)
// ストリーミング用date
const streamArchiveDate = ref<string | null>(null)

/**
 * 現在表示している過去映像の日時
 */
const currentDate = computed(() => {
  const date = formatDateStr(startDate.value.date, 'YYYY/MM/DD HH:mm')
  return `${date}:${formatSecondStr(archiveDisplayTime.value).slice(-2)}`
})

/**
 * ゼロ件表示
 */
const isZeroItem = computed(() => totalCameras.value === 0)

/**
 * 過去映像の表示日時
 */
const archiveStartTime = computed(() => {
  const date = new Date(`${getDateStr(startDate.value.date)} ${startDate.value.time}`)
  return getIsoDateStr(date)
})

/**
 * 画面に表示する映像の再生日時
 */
const displayDate = computed(() => {
  const date = formatDateStr(startDate.value.date, 'YYYY-MM-DD')
  const time = startDate.value.time
  return getIsoDateStr(`${date} ${time}`)
})

/**
 * マルチ映像の用のデータを取得
 */
function getMultiVideoData() {
  resetArchiveRetryState()
  clearPlaybackTimeUpdateTimer()
  pageSize.value = props.division
  isPaused.value = true
  isArchiveLoaded.value = false

  getCameras().then(() => {
    /**
     * 分割数分のカメラの過去映像を取得
     */
    if (permissions.value.archive) {
      getArchives()
    } else {
      // 過去映像の閲覧権限が無い場合は実行しない
      cameras.value = cameraList.value as MultiVideoArchive[] | null
      isArchiveLoaded.value = true
    }
  })
}

/**
 * 過去映像の取得
 */
function getArchives() {
  clearArchiveRetryTimer()
  clearPlaybackTimeUpdateTimer()
  // getMultiVideoDataでもisArchiveLoadedの設定を行っているが、
  // getArchives単体で利用される場合もあるのでこちらでも設定する
  isArchiveLoaded.value = false
  displayCameraData.value = []

  const date = formatDateStr(startDate.value.date, 'YYYY-MM-DD')
  const time = startDate.value.time
  streamArchiveDate.value = getIsoDateStr(`${date} ${time}`)

  isPaused.value = true
  Promise.all(
    (cameraList.value as CameraDetail[]).map((d) => {
      displayCameraData.value.push(d)
      return requestArchives(d.camera_code as string, date, time)
    })
  ).then((res) => {
    // 取得したデータの中から、該当の日時の過去映像を取得
    archiveData.value = res.map((d) => d.data.data)
    cameras.value = formatArchivesData(archiveData.value)

    if (useLive.value) {
      // ライブ配信
      isArchiveLoaded.value = true
    } else {
      // 過去映像表示
      showArchive()
    }
  })
}

/**
 * 過去映像の表示
 * API取得後は has_data に関わらずローディングを終了する。
 * 再取得は次分遷移・映像 load 失敗時に行う。
 */
function showArchive() {
  clearArchiveRetryTimer()
  isArchiveLoaded.value = true
  archiveRetryCount.value = 0
}

/**
 * 過去映像の再取得(AWSの署名有効期限ぎれ対応にも利用)
 */
function retryRequestArchive() {
  if (archivePlayRetryTimeoutId.value !== null) {
    return
  }

  const retry = scheduleArchiveRetry(() => {
    archivePlayRetryTimeoutId.value = null
    getArchives()
  }, archiveRetryCount.value)
  archiveRetryCount.value = retry.retryCount
  archivePlayRetryTimeoutId.value = retry.timeoutId
}

function clearArchiveRetryTimer() {
  cancelArchiveRetry(archivePlayRetryTimeoutId.value)
  archivePlayRetryTimeoutId.value = null
}

function resetArchiveRetryState() {
  clearArchiveRetryTimer()
  archiveRetryCount.value = 0
}

/**
 * 過去映の中から該当の時間の過去映像を取得して、カメラの詳細情報を追加。
 */
function formatArchivesData(archives: Archive[][]) {
  streamArchiveDate.value = displayDate.value
  return mergeArchivesWithCameras(archives, displayCameraData.value, displayDate.value)
}

/**
 * 過去映の表示時間の制御
 */
function setArchiveDisplayTime() {
  clearPlaybackTimeUpdateTimer()
  playbackTimeUpdateTimeoutId.value = schedulePlaybackTimeUpdate(() => {
    playbackTimeUpdateTimeoutId.value = null
    if (!videoElm.value) {
      return
    }
    archiveDisplayTime.value = videoElm.value.currentTime
    const playback = resolvePlaybackTimeUpdate(
      archiveDisplayTime.value,
      duration.value,
      isPaused.value,
      displayDate.value,
      autoPlayCount.value
    )

    if (playback.shouldContinue) {
      setArchiveDisplayTime()
    } else if (playback.nextArchive) {
      // 最後まで再生された場合は停止して、次の過去映像情報を取得
      isPaused.value = true
      // 時間をまたぐ場合は再度データを取得する
      if (playback.nextArchive === 'hour') {
        getNextHourArchives()
        return
      } else {
        getNextMinuteArchives()
      }
      autoPlayCount.value = playback.autoPlayCount
    }
  })
}

function clearPlaybackTimeUpdateTimer() {
  cancelPlaybackTimeUpdate(playbackTimeUpdateTimeoutId.value)
  playbackTimeUpdateTimeoutId.value = null
}

/**
 * 1分後の過去映像を表示
 */
function getNextMinuteArchives() {
  const nextStartDate = getNextMinuteStartDate(displayDate.value)

  // startDateに設定
  startDate.value.date = nextStartDate.date
  startDate.value.time = nextStartDate.time

  // 表示時間を変更

  archiveDisplayTime.value = 0
  // 過去映像を入れ替える
  cameras.value = formatArchivesData(archiveData.value as Archive[][])
  // 次分がすべて has_data:false の場合は再取得する
  if (!cameras.value.find((d) => d.has_data)) {
    isArchiveLoaded.value = false
    retryRequestArchive()
  }
}

/**
 * 次の時間の過去映像を表示(HH:00で取得)
 */
function getNextHourArchives() {
  resetArchiveRetryState()
  const nextStartDate = getNextHourStartDate(displayDate.value)

  // startDateに設定
  startDate.value.date = nextStartDate.date
  startDate.value.time = nextStartDate.time

  // 過去映像を取得
  getArchives()
}

function loadedArchiveData(data: LoadedArchiveDataPayload) {
  videoElm.value = data.videoElm
  duration.value = videoElm.value.duration

  // 現在の再生時間の制御
  archiveDisplayTime.value = videoElm.value.currentTime
  const playback = resolveLoadedArchivePlayback(autoPlayCount.value)
  autoPlayCount.value = playback.autoPlayCount
  // 連続自動再生は60回(60分）まで。コスト的な問題で60回再生したら停止する。
  if (playback.shouldPlay) {
    playVideo()
  }
}

/**
 * 速度、分割数などのナビゲーションの表示切り替え
 */
function toggleNav() {
  isShowToolTip.value = !isShowToolTip.value
}

/**
 * ページの変更
 */
function pageChange(page: number) {
  currentPage.value = page
  getMultiVideoData()
}

/**
 * 再生スピードの変更
 */
function changePlaySpeed(v: number) {
  speed.value = v
}

/**
 * 分割数の変更
 */
function changeDivision(v: number) {
  currentPage.value = 1
  emit('changeDivision', v)
}

/**
 * 映像の再生
 */
function playVideo() {
  isPaused.value = false
  setArchiveDisplayTime()
}

/**
 * 映像の再生、停止の制御
 */
function togglePlayPause() {
  isPaused.value = !isPaused.value
  setArchiveDisplayTime()
}

/**
 * 映像コントローラーの表示
 * フルスクリーンの場合無操作だったらコントローラを非表示にする
 */
function showController() {
  if (keepControllerFlag.value) {
    return
  }
  isShowController.value = true
  cancelControllerHide(mouseActionTimeout.value)
  mouseActionTimeout.value = scheduleControllerHide(() => {
    hideController()
  })
}

function showKeepController() {
  keepControllerFlag.value = true
  isShowController.value = true
  cancelControllerHide(mouseActionTimeout.value)
}

function doNotKeepController() {
  keepControllerFlag.value = false
}

/**
 * 映像コントローラーの非表示
 */
function hideController() {
  isShowController.value = false
  isShowToolTip.value = false
}

/**
 * seekBarを動かした場合の制御
 */
function currentTimeChange() {
  seekBarValue.value = archiveDisplayTime.value
}

/**
 * 過去映像の表示日付の変更
 */
function changeDisplayDate() {
  if (useLive.value) {
    // useLiveが変更されば場合は過去映像を取得するのでgetArchivesを実行しない
    useLive.value = false
  } else {
    resetArchiveRetryState()
    getArchives()
  }
}

/**
 * フルスクリーン表示、解除
 */
function toggleFullScreen() {
  fullScreenFlag.value = toggleFullscreen(
    multiVideos.value as Element,
    fullScreenFlag.value,
    exitFullScreen
  )
}

/**
 * フルスクリーンの解除
 */
function exitFullScreen() {
  fullScreenFlag.value = syncFullscreenFlag(fullScreenFlag.value)
}

/**
 * 最新の映像を表示
 */
function showLastArchive() {
  resetArchiveRetryState()
  startDate.value = getLastDate()
  getArchives()
}

// created() equivalent — run immediately in setup
getMultiVideoData()

// ライブ、過去映像両方の閲覧権限がある場合のみ、storeの情報を利用する
if (permissions.value.live && permissions.value.archive) {
  useLive.value = liveState.value
} else {
  useLive.value = permissions.value.live
}

watch(useLive, (value, oldValue) => {
  // 初期化時の値変更は処理を実行しない
  if (oldValue === null) {
    return
  }

  resetArchiveRetryState()
  clearPlaybackTimeUpdateTimer()
  // ライブ、過去映像の表示状態をstoreに保存
  store.commit('multiVideos/setLiveState', value)

  if (value) {
    isPaused.value = true
  } else {
    getArchives()
  }
})

watch(
  () => props.division,
  () => {
    getMultiVideoData()
  }
)

onUnmounted(() => {
  clearArchiveRetryTimer()
  clearPlaybackTimeUpdateTimer()
  cleanupUiControl(mouseActionTimeout.value, exitFullScreen)
})

expose({
  useLive,
  cameras,
  isArchiveLoaded,
  archiveRetryCount,
  archivePlayRetryTimeoutId,
  showArchive,
  archiveData,
  displayCameraData,
  archiveDisplayTime,
  getNextMinuteArchives,
  getNextHourArchives,
  retryRequestArchive,
  clearArchiveRetryTimer,
  changeDisplayDate,
  pageChange,
  isPaused,
  currentPage,
  changeDivision,
  loadedArchiveData,
  videoElm,
  duration,
  togglePlayPause,
  setArchiveDisplayTime,
  playbackTimeUpdateTimeoutId,
  clearPlaybackTimeUpdateTimer,
  startDate,
  autoPlayCount,
  currentTimeChange,
  seekBarValue,
  showController,
  isShowController,
  showKeepController,
  keepControllerFlag,
  exitFullScreen,
  toggleFullScreen,
  fullScreenFlag,
  getArchives,
  getMultiVideoData,
  playVideo,
  hideController,
  resetArchiveRetryState,
  getLastDate,
  formatArchivesData,
  toggleNav,
  changePlaySpeed,
  showLastArchive,
  currentDate,
  isZeroItem,
  archiveStartTime,
  displayDate,
  cameraList,
  getCameras,
  pageSize,
  totalCameras,
  speed,
  streamArchiveDate,
  isShowToolTip,
  displayToolTipController,
  retriedRequestArchive,
  isArchivePlayRetrying,
  mouseActionTimeout,
  multiVideos,
})

return (_ctx: any,_cache: any) => {
  return (!_unref(isZeroItem))
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(["multi_videos", { full_screen: fullScreenFlag.value }]),
        ref_key: "multiVideos",
        ref: multiVideos,
        "data-test": "multi-videos"
      }, [
        _createVNode(_unref(WhiteBox), { padding: 0 }, {
          default: _withCtx(() => [
            _createElementVNode("div", {
              class: "player",
              onMousemove: showController,
              onMouseleave: hideController
            }, [
              _createVNode(_Transition, null, {
                default: _withCtx(() => [
                  _createVNode(_unref(MultiVideosHeader), {
                    startDate: startDate.value,
                    "onUpdate:startDate": _cache[0] || (_cache[0] = ($event: any) => ((startDate).value = $event)),
                    currentPage: _unref(currentPage),
                    isLoaded: isArchiveLoaded.value,
                    totalCameras: _unref(totalCameras),
                    division: __props.division,
                    fullScreen: fullScreenFlag.value,
                    onChangeDisplayDate: changeDisplayDate,
                    onPageChange: pageChange
                  }, null, 8, ["startDate", "currentPage", "isLoaded", "totalCameras", "division", "fullScreen"])
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_unref(ContentBox), { isLoaded: isArchiveLoaded.value }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Videos), {
                      "camera-list": cameras.value,
                      division: __props.division,
                      paused: isPaused.value,
                      "show-nav": _unref(cameraInfo).showDate,
                      speed: speed.value,
                      "display-date": _unref(displayDate),
                      "use-live": useLive.value && _unref(permissions).live,
                      "total-cameras": _unref(totalCameras),
                      "archive-display-time": seekBarValue.value,
                      "stream-archive-date": streamArchiveDate.value,
                      onRetryRequestArchive: retryRequestArchive,
                      onLoadeddata: loadedArchiveData
                    }, null, 8, ["camera-list", "division", "paused", "show-nav", "speed", "display-date", "use-live", "total-cameras", "archive-display-time", "stream-archive-date"])
                  ]),
                  _: 1
                }, 8, ["isLoaded"]),
                (_unref(cameraList))
                  ? (_openBlock(), _createBlock(_Transition, { key: 0 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", {
                          onMousemove: showKeepController,
                          onMouseleave: doNotKeepController
                        }, [
                          _withDirectives(_createVNode(_unref(PlayerController), {
                            modelValue: archiveDisplayTime.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((archiveDisplayTime).value = $event)),
                            "is-paused": isPaused.value,
                            duration: duration.value,
                            "use-capture": false,
                            "use-live": useLive.value,
                            "onUpdate:use-live": _cache[2] || (_cache[2] = ($event: any) => ((useLive).value = $event)),
                            "use-full-screen": true,
                            "full-screen-flag": fullScreenFlag.value,
                            "onUpdate:full-screen-flag": _cache[3] || (_cache[3] = ($event: any) => ((fullScreenFlag).value = $event)),
                            "can-live": _unref(permissions).live,
                            "is-multi-video": true,
                            speed: speed.value,
                            useSpeedNav: !useLive.value,
                            isArchiveLoaded: isArchiveLoaded.value,
                            division: __props.division,
                            onToggleNav: toggleNav,
                            onTogglePlayPause: togglePlayPause,
                            onChange: currentTimeChange,
                            onFullScreen: toggleFullScreen,
                            onShowLastArchive: showLastArchive,
                            onChangeSpeed: changePlaySpeed,
                            onShowController: showController,
                            onChangeDivision: changeDivision
                          }, null, 8, ["modelValue", "is-paused", "duration", "use-live", "full-screen-flag", "can-live", "speed", "useSpeedNav", "isArchiveLoaded", "division"]), [
                            [_vShow, isShowController.value]
                          ])
                        ], 32)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                  default: _withCtx(() => [
                    (_unref(cameraInfo).showDate)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                          (useLive.value && _unref(permissions).live)
                            ? (_openBlock(), _createBlock(_unref(LiveDisplayTime), { key: 0 }))
                            : (_openBlock(), _createBlock(_unref(ArchiveDisplayTime), {
                                key: 1,
                                "start-time": _unref(archiveStartTime),
                                "is-paused": isPaused.value,
                                "current-time": archiveDisplayTime.value
                              }, null, 8, ["start-time", "is-paused", "current-time"]))
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ])
            ], 32)
          ]),
          _: 1
        })
      ], 2))
    : (_openBlock(), _createBlock(_unref(ZeroItem), {
        key: 1,
        onClear: _cache[4] || (_cache[4] = ($event: any) => (emit('clearFilter')))
      }))
}
}

})